import isToday from "date-fns/isToday";
import isTomorrow from "date-fns/isTomorrow";
import parseISO from "date-fns/parseISO";
import groupBy from "lodash/groupBy";
import orderBy from "lodash/orderBy";
import React, { useState } from "react";

import { Theme, useMediaQuery } from "@material-ui/core";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Breakpoints from "common/breakpoints";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import { useRacehorse360Api } from "hooks/api";
import { SortOrder } from "interfaces/SortOrder";
import DashboardCard from "pages/DashboardPage/DashboardCard";
import {
  currentTimeZoneString,
  differenceInDaysRoundedUp
} from "utils/date-utils";
import NextDaysActivitiesGroup from "./NextDaysActivitiesGroup";
import { TUpcomingActivity } from "./types";

import useStyles from "./styles";

const NEXT_DAYS_QUANTITY = 3;

export interface Props {
  className: string;
  daysQuantity?: number;
  refetchWorkoutRequestsTotalCount: () => void;
}

const NextDaysActivities = (props: Props) => {
  const {
    className,
    daysQuantity = NEXT_DAYS_QUANTITY,
    refetchWorkoutRequestsTotalCount
  } = props;

  const classes = useStyles();
  const matchesDownSX720 = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down(Breakpoints.SX_720)
  );

  const { currentUser } = useLoggedInUser();

  const [isWeatherServiceAvailable, setIsWeatherServiceAvailable] =
    useState(true);

  //TODO: get facility info from API for next 3 days
  const selectedFacility = {
    id: "1",
    latitude: 34.140614,
    longitude: -118.044555,
    code: "SAP",
    backgroundColor: "07F3B2"
  };

  const { useDailyForecast } = useRacehorse360Api();

  const { isLoading: isWeatherLoading, data } = useDailyForecast(
    {
      lat: selectedFacility?.latitude || 0,
      lon: selectedFacility?.longitude || 0,
      cnt: daysQuantity
    },
    {
      enabled: matchesDownSX720,
      onSuccess: () => setIsWeatherServiceAvailable(true),
      onError: () => setIsWeatherServiceAvailable(false)
    }
  );
  const weather = data?.list ? data.list.slice(0, daysQuantity) : [];

  const { useListWorkoutRequests, useListUpcomingRaceEntries } =
    useRacehorse360Api();

  const nextDaysFilter: racehorse360.IDateMatch = {
    range: {
      relative: {
        startDate: {
          value: 0
        },
        endDate: {
          value: NEXT_DAYS_QUANTITY
        },
        timezone: currentTimeZoneString()
      }
    }
  };

  const {
    data: dataUpcomingWorkoutRequests,
    isLoading: isUpcomingWorkoutsLoading,
    refetch: refetchListWorkoutRequests
  } = useListWorkoutRequests(
    {
      query: {
        trainerIds: currentUser.trainerIds,
        date: nextDaysFilter,
        statuses: [
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_REQUESTED,
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_EXAM_PENDING,
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_APPROVED
        ]
      },
      pagingOptions: {
        maxResults: 100
      },
      getOptions: {
        select: [
          "id",
          "date",
          "horse.name",
          "facility",
          "facility.backgroundColor",
          "facility.name",
          "facility.code",
          "facility.shut_off_period_in_hours",
          "facility.shut_off_hours",
          "facility.timezone",
          "status"
        ],
        orderBy: [`date ${SortOrder.ASC}`]
      }
    },
    {
      onError: error => console.error(error)
    }
  );
  const upcomingWorkoutRequests: racehorse360.IWorkoutRequest[] =
    dataUpcomingWorkoutRequests?.workoutRequests || [];

  const {
    data: dataUpcomingRaceEntries,
    isLoading: isUpcomingRaceEntriesLoading
  } = useListUpcomingRaceEntries(
    {
      query: {
        trainerIds: currentUser.trainerIds,
        raceDate: nextDaysFilter
      },
      getOptions: {
        select: [
          "id",
          "horse",
          "horse.trainer.firstName",
          "horse.trainer.middleName",
          "horse.trainer.lastName",
          "raceDate",
          "facility.name",
          "facility.backgroundColor",
          "raceNumber"
        ],
        orderBy: [`raceDate ${SortOrder.ASC}`]
      }
    },
    {
      onError: error => console.error(error)
    }
  );
  const upcomingRaceEntries: racehorse360.IUpcomingRaceEntry[] =
    dataUpcomingRaceEntries?.upcomingRaceEntries || [];

  const isLoading = [
    isUpcomingRaceEntriesLoading,
    isUpcomingWorkoutsLoading
  ].some(Boolean);

  const refetchWorkoutRequests = () => {
    refetchListWorkoutRequests();
    refetchWorkoutRequestsTotalCount();
  };

  const renderActivities = () => {
    const allUpcomingActivities: TUpcomingActivity[] = [
      ...upcomingRaceEntries,
      ...upcomingWorkoutRequests
    ];

    const dateGroups = groupBy(
      allUpcomingActivities,
      upcomingActivity =>
        (upcomingActivity as racehorse360.IWorkoutRequest).date ||
        (upcomingActivity as racehorse360.IUpcomingRaceEntry).raceDate
    );

    return orderBy(Object.keys(dateGroups)).map(dateGroup => {
      const upcomingActivities = dateGroups[dateGroup];
      const date = parseISO(dateGroup);
      const weatherDayIndex = differenceInDaysRoundedUp(date);
      let dateTitle;

      if (isToday(date)) {
        dateTitle = "(Today)";
      } else if (isTomorrow(date)) {
        dateTitle = "(Tomorrow)";
      } else {
        dateTitle = "";
      }

      return (
        <NextDaysActivitiesGroup
          key={dateGroup}
          selectedFacility={selectedFacility}
          upcomingActivities={upcomingActivities}
          date={date}
          dateTitle={dateTitle}
          weather={weather[weatherDayIndex]}
          isWeatherLoading={isWeatherLoading}
          isWeatherServiceAvailable={isWeatherServiceAvailable}
          refetchWorkoutRequests={refetchWorkoutRequests}
        />
      );
    });
  };

  return (
    <DashboardCard
      className={className}
      dataTestId={"nextDaysActivitiesGrid"}
      header={matchesDownSX720 && `Next ${daysQuantity} Days`}
      subHeader={!matchesDownSX720 && `Next ${daysQuantity} Days`}
    >
      {isLoading && <Loader overlay />}
      <div className={classes.scrollable}>{renderActivities()}</div>
    </DashboardCard>
  );
};

export default React.memo(NextDaysActivities);
